import {
  FormControl,
  FormControlProps,
  FormHelperText,
  FormLabel,
} from '@mui/material';
import IncFormHelperText from 'components/forms/inc-form-helper-text/inc-form-helper-text.component';
import {
  FilePond as FilePondInterface,
  FilePondOptions,
  LoadServerConfigFunction,
  ProcessServerConfigFunction,
  ServerUrl,
} from 'filepond';
import React, { useRef } from 'react';
import { FilePond } from 'react-filepond';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { mergeRefs } from 'util/merge-refs';
import { IncFormFieldProps } from '../field.types';
import {
  defaultLoad,
  FILE_UPLOADING_STATUS,
  urlToFilepondFile,
} from './file-upload-field.utils';

export type FileUploadFieldProps = {
  helperText?: string;
  'data-testid'?: string;
  process: string | ServerUrl | ProcessServerConfigFunction;
  load?: string | ServerUrl | LoadServerConfigFunction;
  getId?: (file: any) => string;
  valueToFiles?: (value: any) => Promise<any[]>;
  FormControlProps?: FormControlProps;
  FilePondProps?: FilePondOptions;
  filePondRef?: React.MutableRefObject<FilePondInterface>;
  maxFileSize?: string;
} & IncFormFieldProps;

const FileUploadField = ({
  name,
  helperText,
  label,
  'data-testid': dataTestId = 'FileUploadField',
  process,
  load,
  getId = file => file,
  valueToFiles,
  FormControlProps = {},
  FilePondProps = {},
  filePondRef,
}: FileUploadFieldProps) => {
  const _filePondRef = useRef<any>();
  const { t } = useTranslation();
  const {
    field: { ref, value, ...fieldRest },
    fieldState: { error },
  } = useController({ name });

  return (
    <FormControl
      error={!!error}
      sx={{ mb: 1 }}
      data-testid={dataTestId}
      {...FormControlProps}
    >
      <FormLabel>{label}</FormLabel>
      <FilePond
        ref={
          filePondRef ? mergeRefs([filePondRef, _filePondRef]) : _filePondRef
        }
        {...FilePondProps}
        oninit={async () => {
          let defaultValue;
          if (value) {
            if (valueToFiles) {
              defaultValue = await valueToFiles(value);
            } else {
              defaultValue = Array.isArray(value)
                ? value.map(url => urlToFilepondFile(url))
                : [urlToFilepondFile(value)];
            }
          } else {
            defaultValue = [];
          }
          _filePondRef.current?.addFiles(defaultValue);
        }}
        onaddfilestart={file => {
          if (FilePondProps.allowMultiple) {
            if (!file.serverId)
              fieldRest.onChange([
                ...(value ?? []),
                {
                  id: file.id,
                  status: FILE_UPLOADING_STATUS,
                },
              ]);
          } else {
            if (!value) fieldRest.onChange(FILE_UPLOADING_STATUS);
          }
        }}
        onprocessfile={(error, file) => {
          if (!!error) return;
          fieldRest.onChange(
            FilePondProps.allowMultiple
              ? [...value, file?.serverId].filter(item => item?.id !== file?.id)
              : file?.serverId,
          );
          // how dumb https://github.com/pqina/react-filepond/issues/26
          file.setMetadata('customServerId', file.serverId, true);
        }}
        onerror={() => {
          if (FilePondProps.allowMultiple) {
            fieldRest.onChange(
              value.filter(file => file.status !== FILE_UPLOADING_STATUS),
            );
          } else if (value === FILE_UPLOADING_STATUS) {
            fieldRest.onChange('');
          }
        }}
        onremovefile={(_error, file) => {
          const serverId = file.serverId ?? file.getMetadata('customServerId');
          fieldRest.onChange(
            FilePondProps.allowMultiple
              ? value.filter(savedFile => getId(savedFile) !== getId(serverId))
              : '',
          );
        }}
        server={{
          process,
          load: load ?? defaultLoad(getId),
          revert: (_id, load) => load(),
        }}
        name={name}
        labelIdle={t('FileUploadField.labelIdle')}
      />
      <FormHelperText>
        <IncFormHelperText error={error} helperText={helperText} />
      </FormHelperText>
    </FormControl>
  );
};
export default React.memo(FileUploadField);
